import React, { useEffect, useMemo, useState, useCallback } from "react";
import { getLanguageString as _ } from "misc/lang";
import { DefaultAndAuthProps } from "model/props";
import PageHeader from "components/pageheader/PageHeader";
import cssLayout from "../layout.module.scss";
import css from "./employeesscreen.module.scss";
import { Organisation } from "model/organisation";
import { Home } from "model/home";
import { Staff } from "model/staff";
import {
  apiGetAccountList,
  apiGetStaffList,
  apiStaffDelete,
  apiStaffCreate,
  apiGetHrAccountList,
  apiGetHrEmployeeList,
  apiHrEmployeeDelete,
} from "net/requests";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TextInput from "components/form/TextInput";
import Search from "components/search/Search";
import FormButton from "components/form/FormButton";
import DataTable, { Column } from "components/datatable/DataTable";
import ConfirmDialog from "components/confirmdialog/ConfirmDialog";
import { Paths } from "appConfig";

const EmployeesScreen: React.FC<DefaultAndAuthProps> = (props) => {
  const {
    admin,
    account,
    home,
    homes,
    setAccount,
    setHome,
    loader,
    navigate,
    globalAppMode,
  } = props;

  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [hrSubOrganisations, setHrSubOrganisations] = useState<Organisation[]>(
    [],
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [employees, setEmployees] = useState<Staff[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [newIdentifier, setNewIdentifier] = useState("");
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    item?: Staff | null;
  }>({ open: false, item: null });

  const selectedOrgId = account?.uuid || "";
  const selectedHomeId =
    home?.uuid || (globalAppMode === "HR" ? "NO_SUB_ORG" : "");

  const orgName = home?.name || account?.name || "";
  const title = _("employees", "title_prefix") + orgName;
  const subtitle = _(
    "employees",
    globalAppMode === "HEALTH" ? "health_subtitle" : "subtitle",
  );

  const mode = admin?.is_super_admin
    ? globalAppMode
    : account?.type?.toUpperCase();

  const subOrgs: (Home | Organisation)[] = useMemo(() => {
    if (globalAppMode === "HR") {
      return hrSubOrganisations;
    }
    return homes || [];
  }, [globalAppMode, hrSubOrganisations, homes]);

  const loadOrganisations = useCallback(() => {
    apiGetAccountList(admin, globalAppMode)
      .then((response) => {
        const orgs: Organisation[] = response.data;
        // Only keep top-level organizations (no parent_uuid)
        const topLevelOrgs = orgs.filter((o: Organisation) => !o.parent_uuid);
        setOrganisations(topLevelOrgs);
      })
      .catch((error) => {
        const errorCode = error?.response?.data?.error;
        console.log(errorCode);
      });
  }, [admin, globalAppMode]);

  const loadHrSubOrganisations = useCallback(
    (parent_uuid: string) => {
      console.log("load hr suborganisations for", parent_uuid);
      if (globalAppMode !== "HR" || !parent_uuid) return;

      apiGetHrAccountList(admin, parent_uuid)
        .then((response) => {
          const subOrgs: Organisation[] = response.data;
          setHrSubOrganisations(subOrgs);
        })
        .catch((error) => {
          const errorCode = error?.response?.data?.error;
          console.log(errorCode);
        });
    },
    [admin, globalAppMode],
  );

  const handleSelectOrganisation = (uuid: string) => {
    const org = organisations.find((o) => o.uuid === uuid);
    if (org) {
      setAccount(org as any);
      setHome(undefined);
      if (globalAppMode === "HR") {
        loadHrSubOrganisations(uuid);
      }
    }
  };

  const handleSelectSubOrganisation = (uuid: string) => {
    if (uuid === "NO_SUB_ORG" || uuid === "") {
      setHome(undefined);
      return;
    }
    const selected = subOrgs.find((x) => x.uuid === uuid);
    if (selected) {
      setHome(selected as any);
    }
  };

  const columns: Column[] =
    mode === "HR"
      ? [
          { key: "email", label: _("employees", "email_label") },
          { key: "first_name", label: _("employees", "first_name_label") },
          { key: "last_name", label: _("employees", "last_name_label") },
        ]
      : [{ key: "identifier", label: _("staff", "editor_label_identifier") }];

  const filteredData = employees.filter((item) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    if (globalAppMode === "HR") {
      return (
        (item as any).email?.toLowerCase().includes(q) ||
        (item as any).first_name?.toLowerCase().includes(q) ||
        (item as any).last_name?.toLowerCase().includes(q)
      );
    }
    return item.identifier?.toLowerCase().includes(q);
  });

  const handleOpenCreateModal = () => {
    if (globalAppMode === "HR") {
      const targetAccountId = home?.uuid || account?.uuid;
      navigate(Paths.CREATE_EMPLOYEES, {
        state: { accountId: targetAccountId },
      });
    } else {
      setCreateOpen(true);
    }
  };

  const handleConfirmCreate = () => {
    if (!home) return;
    const value = newIdentifier.trim();
    if (!value) return;
    loader.setLoading();
    apiStaffCreate(admin, home.uuid, value, account?.uuid)
      .then((response) => {
        loader.reset();
        setEmployees((prev) => [...prev, response.data]);
        setCreateOpen(false);
        setNewIdentifier("");
      })
      .catch((error) => {
        loader.reset();
        const errorCode = error.response?.data?.error;
        console.log(errorCode);
      });
  };

  const handleDelete = (item: Staff) => {
    setDeleteDialog({ open: true, item });
  };

  const handleRowClick = (item: Staff) => {
    if (!item?.uuid) return;
    navigate(Paths.EDIT_EMPLOYEES.replace(":id", item.uuid));
  };

  const confirmDelete = () => {
    const item = deleteDialog.item;
    if (!item) return;
    loader.setLoading();

    const deleteApi =
      globalAppMode === "HR"
        ? apiHrEmployeeDelete(admin, item.uuid)
        : apiStaffDelete(admin, item.uuid);

    deleteApi
      .then(() => {
        loader.reset();
        setEmployees((prev) => prev.filter((p) => p.uuid !== item.uuid));
        setDeleteDialog({ open: false, item: null });
      })
      .catch((error) => {
        loader.reset();
        const errorCode = error.response?.data?.error;
        console.log(errorCode);
      });
  };

  const actionLoadEmployees = useCallback(() => {
    if (mode === "HR") {
      // In HR mode, use account ID if no sub-org selected, otherwise use sub-org ID
      const targetId = home?.uuid || account?.uuid;
      if (!targetId) return;

      console.log("ORG IS ", account);
      console.log("sub org is", home);

      console.log("Loading employees for", targetId, "in HR mode");
      apiGetHrEmployeeList(admin, targetId)
        .then((response) => {
          setEmployees(response.data);
        })
        .catch((error) => {
          const errorCode = error.response?.data?.error;
          console.log(errorCode);
        });
    } else {
      if (!home) return;

      apiGetStaffList(admin, home.uuid)
        .then((response) => {
          setEmployees(response.data);
        })
        .catch((error) => {
          const errorCode = error.response?.data?.error;
          console.log(errorCode);
        });
    }
  }, [admin, home, account, mode]);

  useEffect(() => {
    if (admin?.access_token && account) {
      setEmployees([]);
      actionLoadEmployees();
    }
  }, [account, actionLoadEmployees, admin?.access_token, home, globalAppMode]);

  useEffect(() => {
    if (admin?.access_token && admin?.is_super_admin) {
      loadOrganisations();
    }
  }, [admin?.access_token, loadOrganisations, admin?.is_super_admin]);

  useEffect(() => {
    if (admin?.access_token && account?.uuid && globalAppMode === "HR") {
      loadHrSubOrganisations(account.uuid);
    }
  }, [
    admin?.access_token,
    account?.uuid,
    globalAppMode,
    loadHrSubOrganisations,
  ]);

  return (
    <div className={cssLayout.screen}>
      <PageHeader title={title} subtitle={subtitle} />
      <Box className={css.contentBox}>
        <Box className={css.selectRow}>
          <FormControl fullWidth>
            <InputLabel id="org-select-label">
              {_(
                "organisation",
                globalAppMode === "HEALTH"
                  ? "health_organisation_name"
                  : "organisation_name_label",
              )}
            </InputLabel>
            <Select
              labelId="org-select-label"
              value={selectedOrgId}
              label={_(
                "organisation",
                globalAppMode === "HEALTH"
                  ? "health_organisation_name"
                  : "organisation_name_label",
              )}
              onChange={(e) => handleSelectOrganisation(String(e.target.value))}
              disabled={!admin?.is_super_admin}
            >
              {admin?.is_super_admin ? (
                organisations.map((org) => (
                  <MenuItem key={org.uuid} value={org.uuid}>
                    {org.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem key={account?.uuid} value={account?.uuid}>
                  {account?.name}
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="suborg-select-label">
              {_(
                "organisation",
                globalAppMode === "HEALTH"
                  ? "health_sub_organisation_name"
                  : "sub_organisation_name_label",
              )}
            </InputLabel>
            <Select
              labelId="suborg-select-label"
              value={selectedHomeId}
              label={_(
                "organisation",
                globalAppMode === "HEALTH"
                  ? "health_sub_organisation_name"
                  : "sub_organisation_name_label",
              )}
              onChange={(e) =>
                handleSelectSubOrganisation(String(e.target.value))
              }
            >
              {globalAppMode === "HR" && (
                <MenuItem value="NO_SUB_ORG">
                  {_("organisation", "no_sub_organisation_option")}
                </MenuItem>
              )}
              {subOrgs.map((h) => (
                <MenuItem key={h.uuid} value={h.uuid}>
                  {h.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {employees.length === 0 && (
          <div className={css.emptyState}>
            {_(
              "employees",
              globalAppMode === "HEALTH"
                ? "health_empty_state_message"
                : "empty_state_message",
            )}
          </div>
        )}
        <Box className={css.searchRow}>
          <Search
            placeholder={_("general", "search")}
            value={searchQuery}
            onChange={setSearchQuery}
          />
          <FormButton
            label={_("organisation", "create_new")}
            onSubmit={handleOpenCreateModal}
            inline={true}
            variant="contained"
            color="darkerGreen"
          />
        </Box>

        {employees.length > 0 && (
          <div className={css.tableBox}>
            <DataTable
              columns={columns}
              data={filteredData}
              onDelete={handleDelete}
              onRowClick={mode === "HR" ? handleRowClick : undefined}
            />
          </div>
        )}
      </Box>
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        BackdropProps={{
          sx: {
            backgroundColor: "#4e4c4c80",
            backdropFilter: "blur(16px)",
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor: "#FAFAFA",
            borderRadius: "16px",
            minWidth: { xs: "90%", sm: "600px" },
            padding: "16px",
          },
        }}
      >
        <DialogContent>
          <Box className={css.modalHeader}>
            <div className={css.modalTitle}>
              {_("employees", "add_title_prefix") + (home?.name || "...")}
            </div>
            <Button
              onClick={() => setCreateOpen(false)}
              className={css.iconButton}
            >
              <CloseIcon />
            </Button>
          </Box>

          <Box className={css.modalBody}>
            <TextInput
              label={_("employees", "id_label")}
              initialValue={newIdentifier}
              updateValue={setNewIdentifier}
              isRequired={true}
              placeholder={_("employees", "placeholder_id")}
              labelOutside={true}
            />
          </Box>
          <DialogActions className={css.modalActions}>
            <FormButton
              label={_("general", "btn_cancel")}
              onSubmit={() => setCreateOpen(false)}
              heightPx={50}
              color="white"
            />
            <FormButton
              label={_("general", "btn_save")}
              onSubmit={handleConfirmCreate}
              disabled={!home || !newIdentifier.trim()}
              color="darkerGreen"
              heightPx={50}
            />
          </DialogActions>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={deleteDialog.open}
        title={_("general", "btn_delete")}
        message={_("general", "confirm_delete")}
        itemName={deleteDialog.item?.identifier}
        confirmLabel={_("general", "btn_delete")}
        cancelLabel={_("general", "btn_cancel")}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialog({ open: false, item: null })}
      />
    </div>
  );
};

export default EmployeesScreen;
